import { FC, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { locationChange } from '@rikstv/play-common/src/forces/ui/ui.slice';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';

import { useIsAuthenticated } from '../hooks/useIsAuthenticated';

import { useRestoreScrollOnNavigation } from './useScrollRestoration';

// Export hook through a React component so it can easily and safely be passed
// as prop down to CommonRouter
export const OnRouteChange: FC = () => {
  useRouteChange();
  useRestoreScrollOnNavigation();
  return null;
};

let lastLocation: string | undefined;

const useRouteChange = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useIsAuthenticated();

  const logLocationChange = useCallback(
    (pathname: string, isLoggedIn: boolean) => {
      // Avoid multiple events for the same pathname
      const newLocation = [pathname, isLoggedIn].join();
      if (lastLocation !== newLocation) {
        lastLocation = newLocation;
        dispatch(locationChange({ pathname, isLoggedIn }));
      }
    },
    [dispatch]
  );

  const { pathname } = useLocation();

  useEffect(() => {
    // Track screen view on location changed
    authService.shouldRenewTokensOnRouteChange().then(shouldRenew => {
      if (shouldRenew) {
        authService.renewTokens({ redirectUrl: pathname });
      } else {
        logLocationChange(pathname, isLoggedIn);
      }
    });
  }, [pathname, isLoggedIn, logLocationChange]);
};
